import { useEffect, useMemo, useState } from "react";

import { message as toast } from "antd";

import UserAPI from "../Pages/api/UserAPI";
import { getPDFPreviewURL } from "../Utils/file";

export const usePrivateDocuments = (data) => {
  const { userId, documentIds, message, hideToast } = {
    message: `${
      data.documentIds.length > 1 ? "Fetching files..." : "Fetching file..."
    }`,
    hideToast: false,
    ...data,
  };

  const [files, setFiles] = useState([]);
  const [fileUrls, setFileUrls] = useState([]);
  const [filePreviewUrls, setFilePreviewUrls] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const valuesToPass = useMemo(
    () => ({
      isLoading,
      files,
      urls: fileUrls,
      previewUrls: filePreviewUrls,
    }),
    [filePreviewUrls, fileUrls, files, isLoading]
  );

  const fetchAllFileURLs = async () => {
    if (!userId || documentIds.length === 0) return;

    let m;
    if (!hideToast) {
      m = toast.loading(message);
    }

    setIsLoading(true);

    try {
      const filesResp = await Promise.all(
        documentIds.map((docId) =>
          UserAPI.getKYCDocumentStreamById(userId, docId)
        )
      );

      const processedFiles = await Promise.all(
        filesResp.map(async (resp, index) => {
          if (resp instanceof Blob) {
            const url = URL.createObjectURL(resp);
            let previewUrl = url;

            if (resp.type === "application/pdf") {
              previewUrl = await getPDFPreviewURL(resp);
            }

            const fileType = resp.type || "application/octet-stream"; // Fallback type
            const fileExtension = fileType.split("/")[1] || "bin";
            const fileName = `file_${index}.${fileExtension}`;

            return {
              file: new File([resp], fileName, { type: fileType }),
              url,
              previewUrl: previewUrl || url,
            };
          }

          return null;
        })
      );

      const validFiles = processedFiles.filter(Boolean);

      // Update state in a single batch
      setFiles(validFiles.map((file) => file.file));
      setFileUrls(validFiles.map((file) => file.url));
      setFilePreviewUrls(validFiles.map((file) => file.previewUrl));
    } catch (error) {
      console.error("Failed to fetch file:", error);
    } finally {
      setIsLoading(false);
      if (m) m();
    }
  };

  useEffect(() => {
    fetchAllFileURLs();
  }, [userId, documentIds]);

  return valuesToPass;
};

export default usePrivateDocuments;
